import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import WordGrid from '../components/WordGrid';
import WordList from '../components/WordList';
import SessionInfo from '../components/SessionInfo';
import CompletionModal from '../components/CompletionModal';
import { useSocket } from '../context/SocketContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../lib/supabase';

const { FiArrowLeft, FiLoader, FiAlertCircle } = FiIcons;

const GameSession = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [foundWords, setFoundWords] = useState([]);
  const [showCompletion, setShowCompletion] = useState(false);
  const [isHost, setIsHost] = useState(false);

  useEffect(() => {
    loadSession();
  }, [id]);

  useEffect(() => {
    if (socket && id) {
      socket.emit('join-session', id);
      
      socket.on('word-found', (data) => {
        setFoundWords(prev => {
          if (!prev.includes(data.word)) {
            return [...prev, data.word];
          }
          return prev;
        });
      });

      socket.on('session-updated', (updatedSession) => {
        setSession(updatedSession);
      });

      return () => {
        socket.off('word-found');
        socket.off('session-updated');
      };
    }
  }, [socket, id]);

  const loadSession = async () => {
    try {
      const { data, error: supabaseError } = await supabase
        .from('game_sessions_bs7x9k2m1q')
        .select('*')
        .eq('id', id)
        .single();

      if (supabaseError) throw supabaseError;
      if (!data) throw new Error('Session nicht gefunden');

      setSession(data);
      setFoundWords(data.found_words || []);
      
      // Check if user is host
      const hostKey = localStorage.getItem(`host-${id}`);
      setIsHost(!!hostKey);

      // Update last activity
      await supabase
        .from('game_sessions_bs7x9k2m1q')
        .update({ last_activity: new Date().toISOString() })
        .eq('id', id);

    } catch (err) {
      console.error('Error loading session:', err);
      setError('Session konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleWordFound = async (word, coordinates) => {
    if (foundWords.includes(word)) return;

    try {
      const newFoundWords = [...foundWords, word];
      
      const { error: supabaseError } = await supabase
        .from('game_sessions_bs7x9k2m1q')
        .update({ 
          found_words: newFoundWords,
          last_activity: new Date().toISOString()
        })
        .eq('id', id);

      if (supabaseError) throw supabaseError;

      setFoundWords(newFoundWords);

      if (socket) {
        socket.emit('word-found', { sessionId: id, word, coordinates });
      }

      // Check if all words are found
      if (newFoundWords.length === session.words.length) {
        setShowCompletion(true);
      }

    } catch (err) {
      console.error('Error marking word:', err);
    }
  };

  const handleDeleteSession = async () => {
    if (!isHost) return;

    if (window.confirm('Möchtest du diese Session wirklich löschen?')) {
      try {
        const { error: supabaseError } = await supabase
          .from('game_sessions_bs7x9k2m1q')
          .delete()
          .eq('id', id);

        if (supabaseError) throw supabaseError;

        localStorage.removeItem(`host-${id}`);
        navigate('/');
      } catch (err) {
        console.error('Error deleting session:', err);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiLoader} className="text-4xl text-[#2b6777] animate-spin mx-auto mb-4" />
          <p className="text-[#2b6777] font-rubik">Lade Spiel...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiAlertCircle} className="text-4xl text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#2b6777] mb-2 font-rubik">Fehler</h2>
          <p className="text-[#2b6777] mb-4 font-rubik">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#c8d8e4] hover:bg-[#b5c7d1] text-white px-4 py-2 rounded-md transition-colors font-rubik"
          >
            Zur Startseite
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-[#2b6777] hover:opacity-70 transition-opacity font-rubik"
            >
              <SafeIcon icon={FiArrowLeft} />
              Zurück
            </button>
            
            {isHost && (
              <button
                onClick={handleDeleteSession}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors font-rubik"
              >
                Session löschen
              </button>
            )}
          </div>

          <SessionInfo 
            session={session} 
            foundWords={foundWords} 
            isHost={isHost} 
          />

          <div className="grid lg:grid-cols-4 gap-8">
            <div className="lg:col-span-3">
              <WordGrid
                grid={session.grid}
                words={session.words}
                foundWords={foundWords}
                onWordFound={handleWordFound}
              />
            </div>
            <div className="lg:col-span-1">
              <WordList 
                words={session.words} 
                foundWords={foundWords} 
              />
            </div>
          </div>
        </motion.div>
      </div>

      {showCompletion && (
        <CompletionModal
          message={session.completion_message}
          onClose={() => setShowCompletion(false)}
        />
      )}
    </div>
  );
};

export default GameSession;